import type {
  IqeProjectAction,
  IqeProjectStatus,
  TestRun,
} from '../interfaces/ui';

export type TestExecutionActionKey = 'GENERATE' | 'RUN' | 'ENHANCE';

export interface TestExecutionActionOption {
  key: TestExecutionActionKey;
  label: string;
}

export interface ProjectExecutionActionState {
  displayVersion: TestRun | null;
  hasLastRunData: boolean;
  options: TestExecutionActionOption[];
}

export const READY_STATE_ACTIONS: TestExecutionActionOption[] = [
  {
    key: 'GENERATE',
    label: 'Generate and execute Test plan',
  },
];

export const LAST_RUN_ACTIONS: TestExecutionActionOption[] = [
  {
    key: 'RUN',
    label: 'Execute Automation Suite',
  },
  {
    key: 'ENHANCE',
    label: 'Enhance and Execute test plan',
  },
];

export const ACTIVITY_LOG_PHASES = [
  { id: 'plan', label: 'Plan' },
  { id: 'generate&heal', label: 'Generate & Heal' },
  { id: 'run', label: 'Run' },
  { id: 'reporting', label: 'Report' },
] as const;

export function getProjectExecutionActionState(
  runs: TestRun[] | undefined,
  isExecutionInProgress: boolean,
): ProjectExecutionActionState {
  const [latestRun, previousRun] = runs || [];

  const displayVersion =
    (isExecutionInProgress ? previousRun : latestRun) ?? null;

  const hasLastRunData = Boolean(latestRun);
  const options = hasLastRunData ? LAST_RUN_ACTIONS : READY_STATE_ACTIONS;

  return {
    displayVersion,
    hasLastRunData,
    options,
  };
}

export function getLatestRun(runs: TestRun[] | undefined): TestRun | null {
  return runs?.[0] ?? null;
}

export function toMinutesAndSeconds(durationInSeconds: number): string {
  const safeDuration = Math.max(0, Math.floor(durationInSeconds || 0));
  const hours = Math.floor(safeDuration / 3600);
  const minutes = Math.floor((safeDuration % 3600) / 60);
  const seconds = safeDuration % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
}

export function getGenerateOperation(
  status: IqeProjectStatus,
  runCount: number,
): Extract<IqeProjectAction, 'GENERATE' | 'GENERATE_RERUN'> {
  return status === 'GENERATE_FAILED' && runCount === 0
    ? 'GENERATE_RERUN'
    : 'GENERATE';
}

export function getExecuteOperation(
  status: IqeProjectStatus,
  runCount: number,
): Extract<IqeProjectAction, 'EXECUTE' | 'EXECUTE_RERUN'> {
  return status === 'EXECUTE_FAILED' && runCount === 0
    ? 'EXECUTE_RERUN'
    : 'EXECUTE';
}

export function getEnhanceOperation(
  status: IqeProjectStatus,
  runCount: number,
): Extract<IqeProjectAction, 'ENHANCE' | 'ENHANCE_RERUN'> {
  return status === 'ENHANCE_FAILED' && runCount === 0
    ? 'ENHANCE_RERUN'
    : 'ENHANCE';
}

export function getModeFromStatus(
  status: IqeProjectStatus | string | undefined,
): 'generate' | 'execute' | 'enhance' {
  if (
    status === 'ENHANCING' ||
    status === 'ENHANCE_STOPPING' ||
    status === 'ENHANCE_STOPPED'
  )
    return 'enhance';
  if (
    [
      'EXECUTING',
      'EXECUTE_INPROGRESS',
      'EXECUTE_TRIGGERED',
      'EXECUTE_STOPPING',
      'EXECUTE_STOPPED',
    ].includes(status as string)
  )
    return 'execute';
  return 'generate';
}

export function isEnhanceOperation(operation: string | undefined): boolean {
  return operation === 'ENHANCE' || operation === 'ENHANCE_RERUN';
}

export function getModeFromOperation(
  operation: string | undefined,
): 'generate' | 'execute' | 'enhance' {
  if (operation === 'EXECUTE' || operation === 'EXECUTE_RERUN') {
    return 'execute';
  }
  if (isEnhanceOperation(operation)) {
    return 'enhance';
  }
  return 'generate';
}
